import { get } from "lodash-es";
import {
  type Control,
  Controller,
  type FieldError,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
  type PathValue,
} from "react-hook-form";

import { BlInput, type BlInputProps } from "Shared/Inputs/BlInput";

export type BlFormInputProps<FormType extends FieldValues> = Omit<
  BlInputProps,
  "fieldError" | "onChange"
> & {
  name: FieldPath<FormType>;
  control: Control<FormType>;
  errors: FieldErrors<FormType>;
  /** Shorthand for labelProps.label */
  label?: string;
  /** Shorthand for labelProps.required */
  required?: boolean;
  /**
   * When true, transforms the stored value between number (form) and string
   * (display). Combines with maskProps for masked number inputs.
   */
  isNumber?: boolean;
  /**
   * Auto-converts typed characters to uppercase for non-masked inputs.
   * For masked inputs, set maskProps.uppercase instead.
   */
  uppercase?: boolean;
  /** Called with the processed value on every change (side effects). */
  onChange?: (value: string | number | null) => void;
  shouldUnregister?: boolean;
  defaultValue?: PathValue<FormType, FieldPath<FormType>>;
};

export const BlFormInput = <T extends FieldValues>({
  name,
  control,
  errors,
  label,
  required,
  labelProps,
  isNumber,
  uppercase,
  onChange: externalOnChange,
  shouldUnregister,
  defaultValue,
  maskProps,
  ...inputProps
}: BlFormInputProps<T>): React.ReactNode => {
  const mergedLabelProps =
    label !== undefined || required !== undefined
      ? { ...labelProps, label, required }
      : labelProps;
  return (
    <Controller
      control={control}
      name={name}
      shouldUnregister={shouldUnregister}
      defaultValue={defaultValue}
      render={({ field }) => {
        const transformToDisplay = (val: any): string => {
          if (isNumber) {
            if (val === 0) return "0";
            return !val || Number.isNaN(Number(val)) ? "" : val.toString();
          }
          return val === null || val === undefined ? "" : val;
        };

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const raw = uppercase ? e.target.value.toUpperCase() : e.target.value;
          const final = isNumber ? (raw === "" ? null : Number(raw)) : raw;
          field.onChange(final);
          externalOnChange?.(final);
        };

        const handleMaskChange = (val: string) => {
          const final = isNumber ? (val === "" ? null : Number(val)) : val;
          field.onChange(final);
          externalOnChange?.(final);
          maskProps?.onMaskChange?.(val);
        };

        return (
          <BlInput
            name={field.name}
            value={transformToDisplay(field.value)}
            onChange={handleChange}
            onBlur={field.onBlur}
            inputRef={field.ref}
            {...inputProps}
            labelProps={mergedLabelProps}
            maskProps={
              maskProps || isNumber
                ? { ...maskProps, onMaskChange: handleMaskChange }
                : undefined
            }
            isNumber={isNumber}
            fieldError={get(errors, name as string) as FieldError | undefined}
          />
        );
      }}
    />
  );
};
